/** @jsxImportSource @emotion/react */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch } from '@hook/common/useStore';
import IconBlock from '@component/common/block/icon-block';
import ShelfContent from '@component/moashelf/newsletter/shelf-content';
import { useArticleQuery } from '@hook/moashelf/useApi';
import TextBlock from '@component/common/block/text-block';
import { formatDate, removePrefix } from '@util/string';
import { useNavigate } from 'react-router-dom';
import { MOASHELF_PATH } from '@constant/moashelf/route-path';

interface IProps {
  articleKey: string;
}

const ICON_SIZE = '25px';

const Wrapper = styled.div`
  position: relative;
  height: 100%;
`;

const SummaryContainer = styled.div`
  width: calc(100% - 2 * ${size.MARGIN_1});
  margin: ${size.MARGIN_1} auto ${size.MARGIN_1} auto;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${size.WEB_SUB_HEADER_HEIGHT};
  width: 100%;
  padding: 0 ${size.MARGIN_1} 0 ${size.MARGIN_1};
  background-color: ${color.GRAY_4};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const ArticleIconContainer = styled.div`
  display: flex;
  width: fit-content;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
`;

const ICON_DESIGN = {
  box: { width: ICON_SIZE, height: ICON_SIZE, event: { cursor: 'pointer' } },
  icon: { size: 'medium', color: color.GRAY_1, kind: 'outlined' },
} as const;

const Summary: React.FC<any> = ({ title, provider, publishedAt }) => {
  return (
    <SummaryContainer>
      <TextBlock
        text={title}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: true, multiline: 2 },
          box: { width: '100%', height: 'auto', alignX: 'left' },
        }}
      />
      <TextBlock
        text={provider}
        design={{
          text: { size: 'medium' },
          box: { width: '100%', height: 'auto', alignX: 'left' },
        }}
      />
      <TextBlock
        text={formatDate(publishedAt)}
        design={{
          text: {
            size: 'tiny',
            color: color.GRAY_1,
          },
          box: {
            width: '100%',
            height: 'auto',
            alignX: 'left',
          },
        }}
      />
    </SummaryContainer>
  );
};

const Article: React.FC<IProps> = ({ articleKey }) => {
  const dispatch = useTypedDispatch();
  const query = useArticleQuery(articleKey);
  const navigate = useNavigate();

  const content = query?.data?.data?.content || '<div></div>'; // TODO: Loading Spinner
  const title = removePrefix(query?.data?.data?.title ?? '', '[Catch] ');
  const provider = (query?.data?.data?.provider.name || query?.data?.data?.provider.email) ?? '';
  const publishedAt = query?.data?.data?.publishedAt ?? null;

  const handleArticleCloseClick = () => {
    navigate(MOASHELF_PATH.BEGIN_PATH);
  };

  return (
    <Wrapper>
      <Header>
        <ArticleIconContainer />
        <IconBlock icon='close' design={ICON_DESIGN} click={{ handler: handleArticleCloseClick }} />
      </Header>
      <Summary title={title} provider={provider} publishedAt={publishedAt} />
      <Scroll direction='y' height={`calc(100% - ${size.MOBILE_HEADER_HEIGHT} - ${size.WEB_SUB_HEADER_HEIGHT})`}>
        <ShelfContent content={content} />
      </Scroll>
    </Wrapper>
  );
};

export default Article;
